/**
 * Theme-native Gift With Purchase
 * Reads [RM] GWP (rm_gwp) config from Liquid, auto-adds/removes gift variants
 * when eligible cart spend crosses each entry threshold.
 */
(() => {
  const PROPERTY_FLAG = '_gwp';
  const PROPERTY_HANDLE = '_gwp_handle';
  /** Threshold in cents; checkout Functions re-check after discount codes. */
  const PROPERTY_THRESHOLD = '_gwp_threshold';
  /**
   * Checkout UI cannot read underscore line properties. Mirror keys without `_`
   * so checkout can remove/restore the gift when codes drop spend below threshold.
   */
  const PROPERTY_FLAG_PUBLIC = 'rm_gwp';
  const PROPERTY_HANDLE_PUBLIC = 'rm_gwp_h';
  const PROPERTY_THRESHOLD_PUBLIC = 'rm_gwp_th';
  /** Cart attribute: checkout UI only mutates gifts when this is set by the theme block. */
  const CART_ACTIVE_KEY = 'rm_gwp_on';
  const CART_PENDING_KEY = 'rm_gwp_pending';
  /** Percentage off for the Shopify Function discount (100 = free). */
  const PROPERTY_DISCOUNT_PERCENT = '100';
  const routes = window.Shopify?.routes || {};
  const root = routes.root || '/';

  let syncing = false;
  let pending = false;
  let ignoreNextRefresh = false;
  let configCache = null;

  function getConfig() {
    if (configCache) return configCache;
    const el = document.querySelector('[data-gwp-config]');
    if (!el) {
      configCache = [];
      return configCache;
    }
    try {
      const parsed = JSON.parse(el.textContent.trim());
      configCache = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[gwp] invalid config JSON', e);
      configCache = [];
    }
    return configCache;
  }

  function isGwpItem(item) {
    const props = item.properties || {};
    const value = props[PROPERTY_FLAG];
    if (value === undefined || value === null || value === '') return false;
    // Legacy flag + percentage value for the discount function
    return value === true || value === 'true' || !Number.isNaN(Number(value));
  }

  function itemHandle(item) {
    const props = item.properties || {};
    return props[PROPERTY_HANDLE] || null;
  }

  function lineEligible(item, entry) {
    if (isGwpItem(item)) return false;
    if (!entry.restricted) return true;
    const variantIds = entry.variantIds || [];
    const productIds = entry.productIds || [];
    if (variantIds.includes(item.variant_id)) return true;
    if (productIds.includes(item.product_id)) return true;
    return false;
  }

  function eligibleTotal(cart, entry) {
    return (cart.items || []).reduce((sum, item) => {
      if (!lineEligible(item, entry)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function findGiftLine(cart, entry) {
    return (cart.items || []).find((item) => {
      if (!isGwpItem(item)) return false;
      if (itemHandle(item) === entry.handle) return true;
      return item.variant_id === entry.giftVariantId;
    });
  }

  async function fetchCart() {
    const res = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function addGift(entry) {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: entry.giftVariantId,
            quantity: 1,
            properties: {
              [PROPERTY_FLAG]: PROPERTY_DISCOUNT_PERCENT,
              [PROPERTY_HANDLE]: entry.handle,
              [PROPERTY_THRESHOLD]: String(entry.thresholdCents),
              [PROPERTY_FLAG_PUBLIC]: PROPERTY_DISCOUNT_PERCENT,
              [PROPERTY_HANDLE_PUBLIC]: entry.handle,
              [PROPERTY_THRESHOLD_PUBLIC]: String(entry.thresholdCents),
            },
          },
        ],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'cart/add.js failed');
    }
    return res.json();
  }

  async function removeLine(key) {
    const res = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('cart/change.js failed');
    return res.json();
  }

  async function updateCartAttributes(attributes) {
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error('cart/update.js failed');
    return res.json();
  }

  /**
   * Tell checkout whether theme GWP is active. Clear pending + gifts when off.
   * @param {object} cart
   * @param {boolean} active
   */
  async function syncActiveFlag(cart, active) {
    const attrs = cart.attributes || {};
    const currentlyOn = attrs[CART_ACTIVE_KEY] === '1' || attrs[CART_ACTIVE_KEY] === true;
    const hasPending = Boolean(attrs[CART_PENDING_KEY]);
    let nextCart = cart;
    let mutated = false;

    if (active && !currentlyOn) {
      nextCart = await updateCartAttributes({ [CART_ACTIVE_KEY]: '1' });
      mutated = true;
    } else if (!active && (currentlyOn || hasPending)) {
      nextCart = await updateCartAttributes({
        [CART_ACTIVE_KEY]: '',
        [CART_PENDING_KEY]: '',
      });
      mutated = true;
    }

    if (!active) {
      const gifts = (nextCart.items || []).filter((item) => isGwpItem(item));
      for (const gift of gifts) {
        nextCart = await removeLine(gift.key);
        mutated = true;
      }
    }

    return { cart: nextCart, mutated };
  }

  function moneyFromCents(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
    } catch (e) {
      /* fall through */
    }
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  function updateCards(cart, entries) {
    document.querySelectorAll('[data-gwp-root]').forEach((root) => {
      const hideWhenUnlocked = root.getAttribute('data-gwp-hide-when-unlocked') === 'true';
      let visibleCount = 0;

      root.querySelectorAll('[data-gwp-card]').forEach((card) => {
        const handle = card.getAttribute('data-gwp-handle');
        const entry = entries.find((e) => e.handle === handle);
        if (!entry) return;

        const total = eligibleTotal(cart, entry);
        const unlocked = total >= entry.thresholdCents;
        const remaining = Math.max(0, entry.thresholdCents - total);
        const progress = unlocked
          ? 100
          : Math.min(100, Math.max(0, (total / entry.thresholdCents) * 100));

        if (hideWhenUnlocked && unlocked) {
          card.hidden = true;
          return;
        }

        card.hidden = false;
        visibleCount += 1;

        card.setAttribute('data-gwp-unlocked', unlocked ? 'true' : 'false');
        card.setAttribute('data-gwp-progress', String(progress));
        card.style.setProperty('--gwp-progress', String(progress));
        card.classList.toggle('gwp-card--unlocked', unlocked);
        card.classList.toggle('gwp-card--locked', !unlocked);

        const progressBar = card.querySelector('[data-gwp-progress-bar]');
        if (progressBar) {
          progressBar.setAttribute('aria-valuenow', String(Math.round(progress)));
        }

        const status = card.querySelector('.gwp-card__status');
        if (status) {
          if (unlocked) {
            status.textContent =
              card.getAttribute('data-gwp-unlocked-message') || 'Free gift unlocked';
          } else {
            const template =
              card.getAttribute('data-gwp-locked-message') ||
              'Spend [amount] more to unlock';
            const amountHtml = `<span class="gwp-card__remaining" data-gwp-remaining>${moneyFromCents(remaining)}</span>`;
            status.innerHTML = template.includes('[amount]')
              ? template.split('[amount]').join(amountHtml)
              : template;
          }
        }
      });

      if (hideWhenUnlocked) {
        root.hidden = visibleCount === 0;
      }
    });
  }

  function dispatchCartRefresh() {
    ignoreNextRefresh = true;
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  async function sync() {
    if (syncing) {
      pending = true;
      return;
    }

    const entries = getConfig();
    syncing = true;
    let mutated = false;

    try {
      let cart = await fetchCart();
      const active = entries.length > 0;
      const flagResult = await syncActiveFlag(cart, active);
      cart = flagResult.cart;
      mutated = flagResult.mutated || mutated;

      if (!active) {
        if (mutated) dispatchCartRefresh();
        return;
      }

      for (const entry of entries) {
        const total = eligibleTotal(cart, entry);
        const unlocked = total >= entry.thresholdCents;
        const giftLine = findGiftLine(cart, entry);

        if (unlocked && !giftLine) {
          // Avoid adding if same gift variant already in cart without property (manual)
          const existingVariant = (cart.items || []).find(
            (i) => i.variant_id === entry.giftVariantId && !isGwpItem(i)
          );
          if (existingVariant) continue;

          await addGift(entry);
          mutated = true;
          cart = await fetchCart();
        } else if (!unlocked && giftLine) {
          await removeLine(giftLine.key);
          mutated = true;
          cart = await fetchCart();
        }
      }

      updateCards(cart, entries);

      if (mutated) {
        dispatchCartRefresh();
      }
    } catch (e) {
      console.warn('[gwp] sync failed', e);
    } finally {
      syncing = false;
      if (pending) {
        pending = false;
        sync();
      }
    }
  }

  function onCartEvent(event) {
    if (event.type === 'cart:refresh' && ignoreNextRefresh) {
      ignoreNextRefresh = false;
      return;
    }
    // Allow theme cart handlers to settle, then sync gifts
    setTimeout(sync, 50);
  }

  document.addEventListener('cart:change', onCartEvent);
  document.addEventListener('cart:refresh', onCartEvent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(sync, 100));
  } else {
    setTimeout(sync, 100);
  }
})();
